import type { ChatRoom, ChatWithRelations, NewChat } from "@ch/generated";
import {
  addUnreadChat,
  readStoreChatMessage,
  updateAppChatState,
} from "@ch/redux-store/chat/actions";
import { useAppSelector } from "@ch/redux-store/hooks/useAppSelector";
import router from "@ch/router";
import { ChatRoutesEnum } from "@ch/router/ChatRoutes/enums";
import type React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { NotifyCustom, stripHtmlTags } from "../helpers";
import chatSocket from "../sockets/chatSocket";
import { ChatSocketContext } from "./ChatSocketContext";

interface Props {
  children: React.ReactNode;
}

const ChatSocketProvider = ({ children }: Props) => {
  const dispatch = useDispatch();
  const user = useAppSelector((state) => state.user?.currentUser);
  const isLoggedIn = useAppSelector((state) => state.user?.isLoggedIn);
  const filteredCoachIds = useAppSelector(
    (state) => state.chat?.coachIdsWithActiveUserSubscription,
  );

  const onlineMembers = useAppSelector((state) => state.chat?.onlineMembers);

  const [activeChatRoom, setActiveChatRoom] = useState<ChatRoom | null>(null);
  const [incoming, setIncoming] = useState<ChatWithRelations | undefined>(
    undefined,
  );
  const [incomingReadMessage, setIncomingReadMessage] = useState<
    ChatWithRelations | undefined
  >(undefined);
  const id = user?.id;

  const goOnline = useCallback(() => {
    chatSocket.emit("go_online", JSON.stringify({ type: "user", id }));
  }, [id]);

  const goOffline = useCallback(() => {
    chatSocket.emit("go_offline", id ?? "");
  }, [id]);

  const handleJoinRooms = useCallback(
    (data: string[]) => {
      const payload = [...data, id].join(",");
      chatSocket.emit("join_chat_rooms", payload);
    },
    [id],
  );

  useEffect(() => {
    const onConnect = () => {
      if (id) goOnline?.();
    };

    const onDisconnect = (reason: string) => {
      if (reason === "io server disconnect") chatSocket.connect();
    };

    if (isLoggedIn) {
      chatSocket.connect();
      chatSocket.on("connect", onConnect);
      chatSocket.on("disconnect", onDisconnect);
    }

    return () => {
      chatSocket.off("connect", onConnect);
      chatSocket.off("disconnect", onDisconnect);
    };
  }, [goOnline, id, isLoggedIn]);

  useEffect(() => {
    chatSocket.on("online_user", (userId: string) => {
      dispatch(
        updateAppChatState({
          onlineMembers: [
            ...(onlineMembers ?? [].filter((item) => item !== userId)),
            userId,
          ],
        }),
      );
    });

    return () => {
      chatSocket.off("online_user");
    };
  }, [dispatch, onlineMembers]);

  useEffect(() => {
    chatSocket.on("offline_user", (userId: string) => {
      dispatch(
        updateAppChatState({
          onlineMembers: [
            ...(onlineMembers ?? []).filter((item) => item !== userId),
          ],
        }),
      );
    });

    return () => {
      chatSocket.off("offline_user");
    };
  }, [dispatch, onlineMembers]);

  useEffect(() => {
    chatSocket.on("incoming_chat", (message) => {
      const currentPath = window.location.pathname;
      const newchat = JSON.parse(message) as ChatWithRelations;
      if (currentPath === (ChatRoutesEnum.ROOT as string)) {
        setIncoming?.(newchat);
      } else if (!currentPath.includes("authentication")) {
        if (!filteredCoachIds?.includes(newchat?.senderId ?? "")) return;
        NotifyCustom(
          <button
            onClick={() => {
              void router.navigate("/chat");
            }}
            style={{ cursor: "pointer", textAlign: "left" }}
            type="button"
          >
            New message from{" "}
            <span>{newchat?.senderName ?? newchat?.senderId}:</span>
            <p>{stripHtmlTags(newchat?.message ?? "")}</p>
          </button>,
          {
            className: "custom-info-toast",
          },
        );
      }

      if (!newchat.seen?.includes(user?.id ?? "")) {
        dispatch(addUnreadChat(newchat));
      }
    });

    return () => {
      chatSocket.off("incoming_chat");
    };
  }, [dispatch, filteredCoachIds, id, user?.id]);

  useEffect(() => {
    chatSocket.on("incoming_read_chat_message", (message) => {
      const newchat = JSON.parse(message) as ChatWithRelations;
      dispatch(readStoreChatMessage(newchat));
      setIncomingReadMessage?.(newchat);
    });

    return () => {
      chatSocket.off("incoming_read_chat_message");
    };
  }, [dispatch]);

  const sendNewChat = useCallback((chat: NewChat & { frontendId?: string }) => {
    const payload = JSON.stringify(chat);
    chatSocket.emit("new_chat", payload);
  }, []);

  const createChatroom = useCallback((chatroom: ChatRoom) => {
    const payload = JSON.stringify(chatroom);
    chatSocket.emit("new_chat_room", payload);
  }, []);

  const readChatMessage = useCallback(
    (data: { userId?: string; chatId: string }) => {
      const payload = JSON.stringify(data);
      chatSocket.emit("read_chat_message", payload);
    },
    [],
  );

  const props = useMemo(
    () => ({
      chatSocket,
      incoming,
      setIncomingReadMessage,
      setIncoming,
      activeChatRoom,
      setActiveChatRoom,
      handleJoinRooms,
      goOffline,
      goOnline,
      sendNewChat,
      readChatMessage,
      createChatroom,
      incomingReadMessage,
    }),
    [
      activeChatRoom,
      incoming,
      incomingReadMessage,
      createChatroom,
      goOffline,
      goOnline,
      handleJoinRooms,
      readChatMessage,
      sendNewChat,
    ],
  );

  return (
    <ChatSocketContext.Provider value={props}>
      {children}
    </ChatSocketContext.Provider>
  );
};

export default ChatSocketProvider;
